import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLoginContext } from '../../../../contexts/LoginContext';
import { NotificationProfile } from '../../types';
import { useStepperNavigation } from '../../shared/hooks/useStepperNavigation';
import { updateProfileSave } from '../../util';
import { steps } from '../constants/steps';
import { useAlert } from '@schamburg-web/components';

export const useProfileState = () => {
  const {
    user,
    setError,
    setSelectedProfileIndex,
    updateProfile,
    selectedProfileIndex,
    reloadUser,
    setUser,
  } = useLoginContext();

  // Use the shared stepper navigation hook (dynamic step count from steps array)
  const stepperNavigation = useStepperNavigation({ totalSteps: steps.length });
  const { setActiveStep } = stepperNavigation;
  const { showAlert } = useAlert();
  const [isNewProfile, setIsNewProfile] = useState<boolean>(false);

  // Warning message state for validation
  const [warningMessage, setWarningMessage] = useState<string>('');
  const [showWarning, setShowWarning] = useState<boolean>(false);
  const [stepValidations, setStepValidations] = useState<
    Map<number, { message: string; hasErrors: boolean }>
  >(new Map());

  const updateStepValidation = useCallback(
    (stepIndex: number, message: string, hasErrors: boolean) => {
      setStepValidations((prev) => {
        const newMap = new Map(prev);
        newMap.set(stepIndex, { message, hasErrors });
        return newMap;
      });

      // Update warning message and show state based on current step
      if (stepperNavigation.activeStep === stepIndex) {
        setWarningMessage(message);
        setShowWarning(hasErrors && message.length > 0);
      }
    },
    [stepperNavigation.activeStep]
  );

  // Update warning message when active step changes
  useEffect(() => {
    const currentStepValidation = stepValidations.get(
      stepperNavigation.activeStep
    );
    if (currentStepValidation) {
      setWarningMessage(currentStepValidation.message);
      setShowWarning(
        currentStepValidation.hasErrors &&
          currentStepValidation.message.length > 0
      );
    } else {
      setWarningMessage('');
      setShowWarning(false);
    }
  }, [stepperNavigation.activeStep, stepValidations]);

  const [saveError, setSaveError] = useState<string | null>(null);
  const [originalProfile, setOriginalProfile] =
    useState<NotificationProfile | null>(null);

  // Initialize original state when component mounts or profile changes
  useEffect(() => {
    if (user?.extProfiles && selectedProfileIndex !== undefined) {
      const currentProfile = user.extProfiles[selectedProfileIndex];
      if (currentProfile) {
        setOriginalProfile(JSON.parse(JSON.stringify(currentProfile)));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProfileIndex]);

  // URL param handling for testing
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const step = searchParams.get('step');
    if (step) {
      setActiveStep(parseInt(step, 10));
    } else {
      setActiveStep(0);
    }
  }, [searchParams, setSearchParams, setActiveStep]);

  const handleDeleteTempProfile = useCallback(() => {
    if (selectedProfileIndex !== undefined && user?.extProfiles) {
      // Remove the temp profile at the selected index
      const updatedProfiles = user.extProfiles.filter(
        (_, index) => index !== selectedProfileIndex
      );
      const updatedUser = {
        ...user,
        extProfiles: updatedProfiles,
      };
      setUser(updatedUser);

      // Set selected index to 0 or -1 if no profiles remain
      if (setSelectedProfileIndex) {
        setSelectedProfileIndex(updatedProfiles.length > 0 ? 0 : undefined);
      }
    }
  }, [selectedProfileIndex, setSelectedProfileIndex, setUser, user]);

  const handleReset = useCallback(() => {
    if (isNewProfile) handleDeleteTempProfile();
    setSaveError(null);
    setIsNewProfile(false);
    setOriginalProfile(null);
    setActiveStep(0);
    reloadUser();
  }, [handleDeleteTempProfile, isNewProfile, reloadUser, setActiveStep]);

  const handleSave = useCallback(async () => {
    try {
      if (!user.extProfiles || selectedProfileIndex === undefined) {
        throw new Error('No profile selected or profiles not available');
      }
      const res = await updateProfileSave(
        user.extProfiles[selectedProfileIndex] as NotificationProfile // Ensure the selected profile is of type NotificationProfile
      );
      if (res.success) {
        handleReset();
        showAlert('Profile updated successfully!', 'success');
      } else {
        setSaveError(res.exception);
        setError(true);
      }
    } catch (error: unknown) {
      setSaveError(
        error instanceof Error ? error.message : 'An error occurred'
      );
      setError(true);
    }
  }, [
    user.extProfiles,
    selectedProfileIndex,
    handleReset,
    showAlert,
    setError,
  ]);

  return {
    ...stepperNavigation,
    setActiveStep,
    warningMessage,
    showWarning,
    updateStepValidation,
    saveError,
    setSaveError,
    originalProfile,
    setOriginalProfile,
    handleSave,
    handleReset,
    user,
    setError,
    setSelectedProfileIndex,
    updateProfile,
    selectedProfileIndex,
    isNewProfile,
    setIsNewProfile,
  };
};
